import { db, ALL_TABLES } from './db';
import { cascadeDiscardChildren } from './opQueue';

/**
 * Resolve a parked needs-review op. These are pure functions over the op-queue +
 * replica; the provider re-drains + invalidates queries after calling them.
 */

/**
 * Keep the user's change (it loses the conflict because the server row is newer).
 * Re-queue the op pending with a FRESH clientTimestamp so the server's
 * `serverUpdatedAt > clientTime` check passes on the next push, and re-apply the
 * optimistic effect so the user immediately sees their version again.
 */
export async function resolveKeepMine(opId: string): Promise<void> {
  const op = await db.opQueue.get(opId);
  if (!op) return;
  const now = new Date().toISOString();
  await db.transaction('rw', db.opQueue, (db as any)[op.table], async () => {
    const patch: any = { status: 'pending', clientTimestamp: now, message: undefined, retries: 0 };
    // For updates, drop the stored server version. For deletes, KEEP serverRecord
    // — it's the row backup that restores the row if the re-pushed delete fails.
    if (op.operationType !== 'delete') {
      // Re-base the re-push on the SERVER version the user just reviewed (B2):
      // the conflict check becomes "did the server move on AGAIN since I looked",
      // independent of the device clock. If nothing else changed, the re-push
      // applies cleanly; if a third party edited again, it correctly re-conflicts.
      // Falls back to undefined (server uses the fresh clientTimestamp) when no
      // server version was captured.
      patch.baseUpdatedAt = op.serverRecord?.updatedAt;
      patch.serverRecord = undefined;
    }
    await db.opQueue.update(opId, patch);
    const tbl = (db as any)[op.table];
    if (op.operationType === 'update') {
      const current = await tbl.get(op.entityId);
      await tbl.put({ ...(current || { id: op.entityId }), ...op.payload, updatedAt: now, _pendingSync: true });
    } else if (op.operationType === 'delete') {
      await tbl.delete(op.entityId); // re-remove the row we want gone
    }
  });
}

/**
 * Accept the server's version: discard the op. For update/delete the replica
 * already reflects the server (update-conflict reverted at drain; delete-conflict
 * restored the row). For a CREATE conflict (e.g. a duplicate discharge draft the
 * server rejected), "keep server" means adopt the server's existing record — so
 * also drop our optimistic local row (+ any child ops), else a ghost draft lingers
 * in the replica forever. Same cleanup as discardOp.
 */
export async function resolveKeepServer(opId: string): Promise<void> {
  const op = await db.opQueue.get(opId);
  if (op?.operationType === 'create') {
    await discardOp(opId);
    return;
  }
  await db.opQueue.delete(opId);
}

/** Retry a server-rejected ('error') op on the next drain. */
export async function retryOp(opId: string): Promise<void> {
  await db.opQueue.update(opId, { status: 'pending', message: undefined, retries: 0 });
}

/**
 * Drop an op. For a `create`, also remove its optimistic row (it was never
 * created server-side, so discarding means the local row should go too).
 */
export async function discardOp(opId: string): Promise<void> {
  const op = await db.opQueue.get(opId);
  if (!op) return;
  // Wide tx: discarding a parent create cascades to child ops in arbitrary
  // tables (B8), so the scope must cover them all.
  await db.transaction('rw', ALL_TABLES.map((t) => (db as any)[t]), async () => {
    if (op.operationType === 'create') {
      // B8: drop dependent child creates BEFORE the parent, so none is left
      // pointing at a temp id that never reaches the server.
      await cascadeDiscardChildren(op.entityId);
      await (db as any)[op.table].delete(op.entityId);
    }
    await db.opQueue.delete(opId);
  });
}
